import { Card, Chip } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { server } from "../server";
import {
  RiSearchLine,
  RiUser3Fill,
  RiShieldCheckFill,
  RiShieldFlashFill,
  RiGroupLine,
  RiUserFollowLine,

} from "react-icons/ri";

export default function UserList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("pending");
  const [verifiedUsers, setVerifiedUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [unverifiedUsers, setUnverifiedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [verifyingId, setVerifyingId] = useState(null);


  const getAllUsers = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/auth/getallUsers`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
        },
        params: { pageNo: 1, pageSize: 10000, verified: null, admin: false }
      });
      setAllUsers((data?.data || []).filter(u => u.status !== "rejected"));
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to fetch verified users");
    } finally {
      setLoading(false);
    }
  };


  const getAllVerifiedUsers = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/auth/getallUsers`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
        },
        params: { pageNo: 1, pageSize: 10000, verified: true, admin: false }
      });
      setVerifiedUsers((data?.data || []).filter(u => u.status !== "rejected"));
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to fetch verified users");
    } finally {
      setLoading(false);
    }
  };

  const getAllUnverifiedUsers = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/auth/getallUsers`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
        },
        params: { pageNo: 1, pageSize: 10000, verified: false, admin: false }
      });
      setUnverifiedUsers((data?.data || []).filter(u => u.status !== "rejected"));
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to fetch unverified users");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyUser = async (userId) => {
    if (!window.confirm("Are you sure you want to verify this user?")) return;
    try {
      setVerifyingId(userId);
      const { data } = await axios.post(`${server}/auth/verifyUser`, null, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
        },
        params: { userId },
      });
      toast.success(data?.message || "User verified successfully!");
      refreshData();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to verify user");
    } finally {
      setVerifyingId(null);
    }
  };

  const handleRejectUser = async (userId) => {
    const remarks = window.prompt("Enter Remarks for Rejection");
    if (!remarks) {
      toast.error("Remarks are required to reject a user");
      return;
    }
    if (!window.confirm("Are you sure you want to reject this user?")) return;
    try {
      setVerifyingId(userId);
      const { data } = await axios.post(`${server}/auth/rejectUser`, null, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
        },
        params: { userId, remarks },
      });
      toast.success(data?.message || "User Rejected successfully!");
      refreshData();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to reject user");
    } finally {
      setVerifyingId(null);
    }
  };

  const refreshData = () => {
    getAllVerifiedUsers();
    getAllUnverifiedUsers();
    getAllUsers();
  };

  useEffect(() => {
    refreshData();
  }, []);

  const filterList = (list) => {
    return list.filter(
      (user) =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.userName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredVerified = filterList(verifiedUsers);
  const filteredUnverified = filterList(unverifiedUsers);
  const filteredAll = filterList(allUsers);

  const stats = [
    { label: "Total Users", value: allUsers?.length, icon: RiGroupLine, color: "blue", onClick: () => setActiveTab("all") },
    { label: "Verified", value: verifiedUsers?.length, icon: RiUserFollowLine, color: "green", onClick: () => setActiveTab("verified") },
    { label: "Pending", value: unverifiedUsers?.length, icon: RiShieldFlashFill, color: "amber", onClick: () => setActiveTab("pending") },
  ];

  const StatCard = ({ item }) => (
    <Card className="p-4 bg-white border border-slate-200/60 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl">
      <div className="flex items-center gap-4" onClick={item.onClick}>
        <div className={`p-3 rounded-xl bg-${item.color}-50 text-${item.color}-600`}>
          <item.icon className="text-2xl" />
        </div>
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">{item.label}</p>
          <p className="text-2xl  text-slate-800 leading-none">{item.value}</p>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="p-4 lg:p-6 bg-[#fbfcfd] min-h-screen">
      <div className="max-w-[1400px] mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-3xl  text-slate-900 tracking-tight leading-none">User Directory</h1>
            <p className="text-slate-500 font-semibold text-sm">Central command for user verification and identity management.</p>
          </div>

          <div className="relative w-full xl:w-[400px] group transition-all">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <RiSearchLine className="text-slate-400 text-lg group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2.5 w-full border border-slate-200 rounded-xl focus:outline-none focus:ring-[4px] focus:ring-blue-500/5 focus:border-blue-500 bg-white text-slate-800 shadow-sm transition-all text-sm font-bold placeholder:text-slate-400"
            />
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {stats.map((item, idx) => <StatCard key={idx} item={item} />)}
        </div>

        {/* Main Content Card */}
        <Card className="rounded-2xl shadow-xl shadow-blue-900/5 border border-slate-200/50 bg-white overflow-hidden">
          {/* Custom Tabs Navigation */}
          <div className="flex border-b border-slate-100 bg-slate-50/30 p-1.5">
            <button
              onClick={() => setActiveTab("all")}
              className={`flex-1 py-3 px-4 text-xs  transition-all focus:outline-none rounded-xl flex items-center justify-center gap-2 ${activeTab === "all"
                ? "bg-white text-blue-600 shadow-md shadow-blue-500/10"
                : "text-slate-400 hover:text-slate-600 hover:bg-white/50"
                }`}
            >
              <RiGroupLine className={`text-lg ${activeTab === "all" ? "text-blue-600" : "text-slate-300"}`} />
              ALL USERS ({allUsers.length})
            </button>
            <button
              onClick={() => setActiveTab("pending")}
              className={`flex-1 py-3 px-4 text-xs  transition-all focus:outline-none  rounded-xl flex items-center justify-center gap-2 ${activeTab === "pending"
                ? "bg-white text-blue-600 shadow-md shadow-blue-500/10 "
                : "text-slate-400 hover:text-slate-600 hover:bg-white/50"
                }`}
            >
              <RiShieldFlashFill className={`text-lg ${activeTab === "pending" ? "text-blue-600" : "text-slate-300"}`} />
              PENDING ({unverifiedUsers.length})
            </button>
            <button
              onClick={() => setActiveTab("verified")}
              className={`flex-1 py-3 px-4 text-xs  transition-all focus:outline-none rounded-xl flex items-center justify-center gap-2 ${activeTab === "verified"
                ? "bg-white text-blue-600 shadow-md shadow-blue-500/10"
                : "text-slate-400 hover:text-slate-600 hover:bg-white/50"
                }`}
            >
              <RiShieldCheckFill className={`text-lg ${activeTab === "verified" ? "text-blue-600" : "text-slate-300"}`} />
              VERIFIED ({verifiedUsers.length})
            </button>
          </div>

          <div className="p-4 lg:p-6">
            {activeTab === "all" ? (
              /* All Users Tab View */
              <div className="overflow-x-auto rounded-xl border border-slate-100">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-[10px]  text-slate-400 uppercase tracking-wider border-b border-slate-100">
                      <th className="px-6 py-4">User</th>
                      <th className="px-6 py-4">Username</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4 text-right">Verification</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {filteredAll.map(user => (
                      <tr
                        key={user.id}
                        className="hover:bg-blue-50/20 text-sm cursor-pointer transition-colors"
                        onClick={() => navigate("../userDetails", { state: { user } })}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500  text-xs">
                              {user.name?.charAt(0) || "U"}
                            </div>
                            <span className="font-bold text-slate-800 uppercase tracking-tight">{user.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 font-bold text-slate-600">{user.userName}</td>
                        <td className="px-6 py-4">
                          {user.verified ? (
                            <div className="flex items-center gap-1.5 px-2 py-1 bg-green-50 text-green-600 rounded-lg border border-green-100/50 w-fit">
                              <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                              <span className="text-[10px] ">VERIFIED</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-50 text-amber-600 rounded-lg border border-amber-100/50 w-fit">
                              <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                              <span className="text-[10px] ">PENDING</span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                          {user.verified ? (
                            <Chip label="SECURED" size="small" className="!bg-blue-50 !text-blue-600 ! !text-[9px] !rounded-lg" />
                          ) : (
                            <button
                              onClick={() => handleVerifyUser(user.id)}
                              className="text-[10px] font-bold text-blue-600 hover:underline"
                            >
                              Verify Now
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : activeTab === "pending" ? (
              /* Pending Tab View */
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {loading && unverifiedUsers.length === 0 ? (
                  <div className="col-span-full py-20 text-center space-y-4">
                    <div className="animate-spin h-10 w-10 border-[4px] border-blue-600 border-t-transparent rounded-full mx-auto"></div>
                    <p className="text-slate-500 font-bold text-sm">Syncing...</p>
                  </div>
                ) : filteredUnverified.length === 0 ? (
                  <div className="col-span-full py-20 text-center space-y-4 bg-slate-50/50 rounded-2xl border-2 border-dashed border-slate-100">
                    <RiShieldCheckFill className="text-4xl text-slate-200 mx-auto" />
                    <p className="text-slate-500 font-bold">Queue Empty</p>
                  </div>
                ) : (
                  filteredUnverified.map(user => (
                    <div
                      key={user.id}
                      className="p-4 border border-slate-100 rounded-2xl bg-white hover:border-blue-300 hover:shadow-lg transition-all duration-300 flex flex-col sm:flex-row sm:items-center justify-between gap-4 cursor-pointer"
                      onClick={() => navigate("../userDetails", { state: { user } })}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-700 flex items-center justify-center text-white text-xl  shadow-md">
                          {user.name?.charAt(0) || "U"}
                        </div>
                        <div className="space-y-0.5">
                          <h3 className="font-bold text-slate-900 text-lg uppercase tracking-tight leading-none">{user.name}</h3>
                          <p className="text-[11px] font-bold text-blue-500 font-mono">{user.userName}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleRejectUser(user.id)}
                          disabled={verifyingId === user.id}
                          className="px-4 py-2 border border-slate-100 text-slate-400 text-[11px]  rounded-xl hover:bg-red-50 hover:text-red-500 transition-all disabled:opacity-50"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => handleVerifyUser(user.id)}
                          disabled={verifyingId === user.id}
                          className="px-5 py-2 bg-blue-600 text-white text-[11px]  rounded-xl hover:bg-blue-700 hover:shadow-md transition-all disabled:bg-slate-200"
                        >
                          {verifyingId === user.id ? "Wait..." : "Verify"}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              /* Verified Tab View */
              <div className="overflow-x-auto rounded-xl border border-slate-100">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-[10px]  text-slate-400 uppercase tracking-wider border-b border-slate-100">
                      <th className="px-6 py-4">User</th>
                      <th className="px-6 py-4">Contact</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {filteredVerified.map(user => (
                      <tr
                        key={user.id}
                        className="hover:bg-blue-50/20 text-sm cursor-pointer transition-colors"
                        onClick={() => navigate("../userDetails", { state: { user } })}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500  text-xs">
                              {user.name?.charAt(0) || "V"}
                            </div>
                            <span className="font-bold text-slate-800 uppercase tracking-tight">{user.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 font-bold text-slate-600">{user.mobile || "---"}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5 px-2 py-1 bg-green-50 text-green-600 rounded-lg border border-green-100/50 w-fit">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                            <span className="text-[10px] ">ACTIVE</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <Chip label="SECURED" size="small" className="!bg-blue-50 !text-blue-600 ! !text-[9px] !rounded-lg" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f8fafc;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }
      `}</style>
    </div>
  );
}
